<script setup>
// @ts-check

import BaseIcon from '@/components/base/BaseIcon.vue'
import { useLanguageStore } from '@/store/language'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

const props = defineProps({
  /**
   * Render logo as a link to ukko.fi webpage
   */
  link: {
    type: Boolean
  },
  /**
   * Render white logo
   */
  white: {
    type: Boolean
  },
  /**
   * Render extra small size logo
   */
  xsmall: {
    type: Boolean
  },
  /**
   * Render small size logo
   */
  small: {
    type: Boolean
  },
  /**
   * Render medium size logo
   */
  medium: {
    type: Boolean
  },
  /**
   * Render large size logo
   */
  large: {
    type: Boolean
  },
  /**
   * Render extra large size logo
   */
  xlarge: {
    type: Boolean
  },
  plainLogo: {
    type: Boolean
  }
})

defineOptions({
  name: 'ServiceLogo'
})

const store = useStore()
const { t } = useI18n()
const languageStore = useLanguageStore()
const { language } = storeToRefs(languageStore)

const service = computed(() => store.state.app.service)

const logo = computed(() => {
  if (props.plainLogo) {
    return 'logo_plain.svg'
  }
  return language.value === 'en' ? 'logo_en.svg' : 'logo_fi.svg'
})

const size = computed(() => {
  if (props.xsmall) {
    return 'h-8'
  }

  if (props.small) {
    return 'h-12'
  }

  if (props.medium) {
    return 'h-14'
  }

  if (props.large) {
    return 'h-20'
  }

  if (props.xlarge) {
    return 'h-24'
  }

  return 'h-16'
})
</script>

<template>
  <div
    class="service-logo-wrapper flex flex-initial justify-start justify-items-start justify-self-start"
    :class="size"
  >
    <a
      :aria-label="t('general.service_aria_label')"
      class="logo-container flex max-h-full justify-start justify-items-start justify-self-start"
      :href="link ? t('general.ukko_link') : '/'"
      target="_blank"
      rel="noopener noreferrer"
      :class="size"
    >
      <BaseIcon
        custom-size
        :svg="require(`@/assets/images/${service}/${logo}?inline`)"
        class="block"
        :class="{
          [`logo-${service}`]: true,
          'text-blue': !white,
          'text-white': white
        }"
      />
    </a>
  </div>
</template>

<style lang="scss" scoped>
@reference '../../styles/main.css';

.service-logo-wrapper {
  @apply max-w-full max-h-full;
}
.service-logo-wrapper * svg {
  @apply max-h-full w-full h-full;
}
</style>
